import { Breadcrumbs, Chip, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ForwardIcon } from 'src/components/icons';
import { useEscNavigation } from 'src/hooks/useEscNavigation';
import useNavigate from 'src/hooks/useNavigate';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BlackHeadings,
  GraySmall,
  HoverNonBorderBackground,
} from 'src/theme/colors';
import { RegularButtonHoverShadow } from 'src/theme/shadows';

const useStyles = makeStyles()((theme) => ({
  breadcrumbsRoot: {
    overflow: 'hidden',
  },
  breadcrumbsListContainer: {
    flexWrap: 'nowrap',
  },
  compactBreadcrumbsRoot: {
    display: 'block',
    width: '100%',
    minWidth: 0,
  },
  compactBreadcrumbsRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    // Scroll the trail horizontally, using the app's default scrollbar, when
    // it is wider than the available header space instead of truncating crumbs.
    overflowX: 'auto',
  },
  compactCrumb: {
    // Keep each crumb at its natural width so the row scrolls rather than
    // shrinking or truncating any single crumb.
    flex: '0 0 auto',
  },
  compactRootLabel: {
    whiteSpace: 'nowrap',

    // clear out button styles
    appearance: 'none',
    border: 'none',
    outline: 'none',
    background: 'none',
    padding: 0,

    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  breadcrumbListItem: {
    '&:last-child': {
      overflow: 'hidden',
    },
  },
  separator: {
    margin: theme.spacing(0, 1.5),
    color: BlackHeadings,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, 0.5),
    },
    '& svg': {
      height: '7px',
      width: '7px',
    },
  },
  node: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',

    // clear out button styles
    appearance: 'none',
    border: 'none',
    outline: 'none',
    background: 'none',
    padding: 0,

    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  pointer: {
    cursor: 'pointer',
    '&:first-child .MuiChip-label': {
      paddingLeft: '0 !important',
    },
  },
  default: {
    cursor: 'default',
    color: BlackHeadings,
  },
}));
const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'transparent',
  cursor: 'pointer',
  height: theme.spacing(3),
  color: GraySmall,
  borderRadius: theme.shape.borderRadius,
  fontWeight: theme.typography.fontWeightRegular,
  '& > .MuiChip-label': {
    padding: theme.spacing(0, 0.5),
  },
  '&:hover, &:focus': {
    backgroundColor: HoverNonBorderBackground,
  },
  '&:active': {
    boxShadow: RegularButtonHoverShadow,
  },
}));

export interface BreadCrumbItem {
  label: string;
  onClick?: () => void;
  path?: string;
  textTransform?:
    | 'capitalize'
    | 'full-size-kana'
    | 'full-width'
    | 'lowercase'
    | 'none'
    | 'uppercase';
}
export const BreadCrumbItem: React.FC<
  BreadCrumbItem & { allowEllipsis?: boolean }
> = ({ label, onClick, path, allowEllipsis = true }) => {
  const { classes } = useStyles();
  const { navigate } = useNavigate();

  const handleClick = React.useCallback(() => {
    if (path) {
      navigate(path);
    } else if (onClick) {
      onClick();
    }
  }, [navigate, onClick, path]);

  const labelClassName = [
    allowEllipsis ? classes.node : classes.compactRootLabel,
    path || onClick ? classes.pointer : classes.default,
  ].join(' ');

  // if path is not provided, then wrap the label in a chip, otherwise, wrap the label in normal text
  return path || onClick ? (
    <StyledChip
      key={path}
      label={
        <BaseTypography fontType="13Medium" className={labelClassName}>
          {label}
        </BaseTypography>
      }
      onClick={handleClick}
    />
  ) : (
    <BaseTypography
      className={labelClassName}
      component="span"
      fontType="13Medium"
      onClick={onClick}
    >
      {label}
    </BaseTypography>
  );
};
export interface BreadCrumbBaseProps {
  items: Prettify<BreadCrumbItem>[];
  showOnlyTitleOnMobile?: boolean;
  /**
   * App Builder header mode: crumbs keep their natural width and the trail
   * scrolls horizontally when sibling header content constrains the row,
   * rather than truncating.
   */
  compact?: boolean;
}
export const BreadCrumbBase: React.FC<BreadCrumbBaseProps> = ({
  items,
  showOnlyTitleOnMobile = false,
  compact = false,
}) => {
  const { classes } = useStyles();
  const previousPage = items.at(items.length - 2);

  // Prefer the previous crumb's path, falling back to its onClick (e.g. iframe
  // extensions send breadcrumbs with onClick-only via the postMessage bridge).
  const escTarget = previousPage?.path ?? previousPage?.onClick;
  useEscNavigation(escTarget ?? '', {
    disabled: items.length === 1 || !escTarget,
  });

  const theme = useTheme();
  const isExtraSmallViewport = useMediaQuery(theme.breakpoints.down('xs'));

  // if showOnlyTitleOnMobile is true, then show only the last item in the breadcrumb
  // this is done to make sure that the breadcrumb does not take up too much space on mobile
  const formattedBreadcrumbItems =
    isExtraSmallViewport && showOnlyTitleOnMobile ? items.slice(-1) : items;

  // Compact rows scroll horizontally when the trail overflows; keep the leaf
  // (current page) pinned into view so it isn't the crumb that scrolls off.
  const compactRowRef = React.useRef<HTMLDivElement>(null);
  const compactTrailKey = formattedBreadcrumbItems
    .map(({ label }) => label)
    .join('>');
  React.useEffect(() => {
    if (!compact) return;
    const row = compactRowRef.current;
    if (row) row.scrollLeft = row.scrollWidth;
  }, [compact, compactTrailKey]);

  if (compact) {
    return (
      <nav aria-label="breadcrumb" className={classes.compactBreadcrumbsRoot}>
        <div ref={compactRowRef} className={classes.compactBreadcrumbsRow}>
          {formattedBreadcrumbItems.map(
            ({ label, onClick, path, textTransform }, idx) => (
              <React.Fragment key={path ?? `${idx}-${label}`}>
                {idx > 0 && (
                  <span className={classes.separator} aria-hidden>
                    <ForwardIcon />
                  </span>
                )}
                <div className={classes.compactCrumb}>
                  <BreadCrumbItem
                    label={label}
                    onClick={onClick}
                    path={path}
                    textTransform={textTransform}
                    allowEllipsis={false}
                  />
                </div>
              </React.Fragment>
            ),
          )}
        </div>
      </nav>
    );
  }

  return (
    <Breadcrumbs
      classes={{
        separator: classes.separator,
        root: classes.node,
        ol: classes.breadcrumbsListContainer,
        li: classes.breadcrumbListItem,
      }}
      className={classes.breadcrumbsRoot}
      separator={<ForwardIcon />}
      aria-label="breadcrumb"
      color="initial"
    >
      {formattedBreadcrumbItems.map(
        ({ label, onClick, path, textTransform }, idx) => {
          const isClickable = !!(path || onClick);
          return (
            <div
              key={path ?? `${idx}-${label}`}
              className={[
                // Non-clickable crumbs get inner padding to align with the
                // chip's label padding.
                !isClickable ? 'px-[5px]' : '',
                !isClickable
                  ? 'truncate max-w-[120px] min-[385px]:max-w-full'
                  : '',
                idx === 0 ? 'pl-[0px]' : '',
              ].join(' ')}
            >
              <BreadCrumbItem
                key={label}
                label={label}
                onClick={onClick}
                path={path}
                textTransform={textTransform}
              />
            </div>
          );
        },
      )}
    </Breadcrumbs>
  );
};
